import React, { useContext, useReducer, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail } from 'lucide-react';
import { Store } from '../../Store';
import { getError } from '../../utils';
import LoadingBox from '../../components/LoadingBox';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';
import ConfirmModal from '../../components/ConfirmModal';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loadingFetch: true };
    case 'FETCH_FAIL':
      return { ...state, loadingFetch: false, errorFetch: action.payload };
    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };
    default:
      return state;
  }
};

function getCategorias(descripcion) {
  switch (descripcion) {
    case 'Rugby':
      return [
        'Infantiles',
        'Juveniles M13',
        'Juveniles M15',
        'Juveniles M17',
        'Mayores',
        'Femenino',
      ];
    case 'Futbol':
      return [
        'Sub14',
        'Sub16',
        'Sub18 1er Año',
        'Sub18 2do Año',
        'Sub20',
        'Reserva',
        'Mayor',
        'Pre Senior',
      ];
    case 'Hockey':
      return [
        'Escuela',
        'Juv. Principiantes',
        'Sub16',
        'Intermedia B',
        'Intermedia C',
        'Mami Hockey',
      ];
    default:
      return ['Administracion'];
  }
}

function NuevoVendedor() {
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [nombre, setNombre] = useState();
  const [descripcion, setDescripcion] = useState();
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [esMenor, setMenor] = useState(false);
  const [adultoResponsable, setAdultoResponsable] = useState(null);

  const [categoria, setCategoria] = useState('');
  const [categorias, setCategorias] = useState(getCategorias(userInfo.descripcion));

  const [showModal, setShowModal] = useState(false);
  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  const [{ loadingCreate }, dispatch] = useReducer(reducer, {
    loadingCreate: false,
    error: '',
  });

  const handleSubmit = _e => {
    const form = _e.currentTarget;
    if (form.checkValidity() === false) {
      _e.preventDefault();
      _e.stopPropagation();
    } else {
      _e.preventDefault();
      _e.stopPropagation();
      handleShow();
    }
  };

  const addVendedorHandler = async () => {
    handleClose();
    try {
      dispatch({ type: 'CREATE_REQUEST' });
      await axios.post(
        '/api/vendedores/crearVendedor',
        {
          nombre,
          telefono,
          email,
          descripcion: descripcion ? descripcion : userInfo.descripcion,
          categoria,
          adultoResponsable,
          esMenor,
        },
        {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );
      dispatch({ type: 'CREATE_SUCCESS' });
      toast.success('Vendedor creado');
      navigate('/AdminScreen/vendedores');
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: 'CREATE_FAIL',
      });
    }
  };

  return (
    <div className='max-w-2xl mx-auto space-y-6 p-6'>
      {loadingCreate && <LoadingBox />}
      <ConfirmModal
        show={showModal}
        title='Crear Vendedor'
        message='Seguro desea crear Vendedor?'
        onConfirm={addVendedorHandler}
        onCancel={handleClose}
        confirmLabel='Confirmar'
        cancelLabel='Cancelar'
      />
      <Helmet>
        <title>Crear Vendedor</title>
      </Helmet>

      <PageHeader
        title='Crear Vendedor'
        actions={
          <Button variant='outline' size='sm' onClick={() => navigate(-1)}>
            <ArrowLeft className='h-4 w-4' />
            Atras
          </Button>
        }
      />

      <Card>
        <CardContent className='pt-6'>
          <form noValidate onSubmit={handleSubmit} className='space-y-4'>
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                id='esMenor'
                onChange={() => setMenor(!esMenor)}
                className='h-4 w-4 rounded border-input accent-primary'
              />
              <label htmlFor='esMenor' className='text-sm font-medium text-foreground'>
                Vendedor Menor de Edad
              </label>
            </div>

            {esMenor && (
              <div className='space-y-1'>
                <label htmlFor='adultoResponsable' className='text-sm font-medium text-foreground'>
                  Adulto Responsable
                </label>
                <input
                  id='adultoResponsable'
                  type='text'
                  required
                  placeholder='Ingresa nombre del adulto responsable'
                  onChange={e => setAdultoResponsable(e.target.value)}
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                />
              </div>
            )}

            <div className='space-y-1'>
              <label htmlFor='nombre' className='text-sm font-medium text-foreground'>
                Nombre del Vendedor
              </label>
              <input
                id='nombre'
                type='text'
                required
                onChange={e => setNombre(e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1'>
                <label htmlFor='telefono' className='text-sm font-medium text-foreground'>
                  Telefono
                </label>
                <div className='relative flex items-center'>
                  <Phone className='absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none' />
                  <input
                    id='telefono'
                    type='text'
                    placeholder='Telefono'
                    required
                    onChange={e => setTelefono(e.target.value)}
                    className='flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  />
                </div>
              </div>

              <div className='space-y-1'>
                <label htmlFor='email' className='text-sm font-medium text-foreground'>
                  E-mail
                </label>
                <div className='relative flex items-center'>
                  <Mail className='absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none' />
                  <input
                    id='email'
                    type='email'
                    placeholder='E-mail'
                    required
                    onChange={e => setEmail(e.target.value)}
                    className='flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  />
                </div>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1'>
                <label htmlFor='descripcion' className='text-sm font-medium text-foreground'>
                  Selecciona un descripcion
                </label>
                <select
                  id='descripcion'
                  disabled={!userInfo.isSuperAdmin}
                  defaultValue={!userInfo.isSuperAdmin ? userInfo.descripcion : 'Otro'}
                  onChange={e => {
                    setDescripcion(e.target.value);
                    setCategorias(getCategorias(e.target.value));
                  }}
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70 disabled:cursor-not-allowed'
                >
                  <option value='Rugby'>Rugby</option>
                  <option value='Futbol'>Futbol</option>
                  <option value='Hockey'>Hockey</option>
                  <option value='Otro'>Otro</option>
                </select>
              </div>

              <div className='space-y-1'>
                <label htmlFor='categoria' className='text-sm font-medium text-foreground'>
                  Categoria
                </label>
                <select
                  id='categoria'
                  required
                  onChange={e => setCategoria(e.target.value)}
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                >
                  <option value=''>Selecciona categoria del vendedor</option>
                  {categorias.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button type='submit' className='w-full'>
              Crear Vendedor
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default NuevoVendedor;
